import React, { useEffect, useState, useMemo } from 'react';
import { Rocket, Building2, TrendingUp, Loader2, ChevronDown, ChevronUp, Globe, Layers, MapPin } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard, useCompanyFilters, CompanyFilterBar, CompanyTypeBadge } from './IntelligenceLayout';
import { WatchlistButton } from '../components/WatchlistButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LABEL_STYLES = {
  'Strong Expansion': 'bg-green-100 text-green-700 border-green-200',
  'Moderate Expansion': 'bg-blue-100 text-blue-700 border-blue-200',
  'Early Signals': 'bg-amber-100 text-amber-700 border-amber-200',
};

const SIGNAL_TYPE_COLORS = {
  'Geographic Expansion': 'bg-blue-50 text-blue-700 border-blue-200',
  'Multi-Department Hiring': 'bg-purple-50 text-purple-700 border-purple-200',
  'Hiring Acceleration': 'bg-red-50 text-red-700 border-red-200',
  'New Market Entry': 'bg-teal-50 text-teal-700 border-teal-200',
  'High-Volume Hiring': 'bg-orange-50 text-orange-700 border-orange-200',
};

const ExpansionSignals = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [sortField, setSortField] = useState('expansion_score');
  const [sortDir, setSortDir] = useState('desc');
  const { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams } = useCompanyFilters();

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/expansion-signals?limit=200${filterParams}`)
      .then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, [excludeStaffing, excludeRetail]);

  const handleSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(f); setSortDir('desc'); }
  };

  const companies = data?.companies || [];
  const sorted = useMemo(() => [...companies].sort((a, b) => {
    const m = sortDir === 'desc' ? -1 : 1;
    if (sortField === 'company') return m * a.company.localeCompare(b.company);
    return m * ((a[sortField] || 0) - (b[sortField] || 0));
  }), [companies, sortField, sortDir]);

  const labels = data?.label_distribution || {};
  const signalTypes = data?.signal_type_distribution || {};

  const SortHeader = ({ field, label, align = 'left' }) => (
    <th className={`text-${align} px-3 py-3 cursor-pointer hover:text-[#0A1F44] select-none whitespace-nowrap text-xs`}
      onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">{label}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  return (
    <IntelligenceLayout title="Expansion Signals" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-emerald-50 rounded-xl flex items-center justify-center">
            <Rocket className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="expansion-title">Expansion & Growth Signals</h1>
            <p className="text-sm text-gray-500">Companies showing expansion patterns — potential hiring increase ahead</p>
          </div>
        </div>
      </header>

      <main className="p-8 space-y-6">
        <CompanyFilterBar excludeStaffing={excludeStaffing} setExcludeStaffing={setExcludeStaffing}
          excludeRetail={excludeRetail} setExcludeRetail={setExcludeRetail} />
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Detecting expansion signals...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="expansion-metrics">
              <MetricCard label="Total Detected" value={data?.total_detected || 0} icon={Rocket} />
              <MetricCard label="Strong Expansion" value={labels['Strong Expansion'] || 0} icon={TrendingUp} />
              <MetricCard label="Moderate" value={labels['Moderate Expansion'] || 0} icon={Globe} />
              <MetricCard label="Early Signals" value={labels['Early Signals'] || 0} icon={Layers} />
            </div>

            {/* Signal type distribution */}
            <div className="flex flex-wrap gap-2" data-testid="signal-type-chips">
              {Object.entries(signalTypes).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                <span key={type} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${SIGNAL_TYPE_COLORS[type] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                  {type}: {count}
                </span>
              ))}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="expansion-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50/30">
                      <th className="text-left px-3 py-3 w-8">#</th>
                      <SortHeader field="company" label="Company" />
                      <SortHeader field="expansion_score" label="Score" align="center" />
                      <th className="text-center px-3 py-3">Label</th>
                      <SortHeader field="total_jobs_30d" label="Jobs (30d)" align="right" />
                      <SortHeader field="cities_count" label="Cities" align="right" />
                      <SortHeader field="states_count" label="States" align="right" />
                      <SortHeader field="departments_count" label="Depts" align="right" />
                      <th className="text-left px-3 py-3">Industry</th>
                      <SortHeader field="signal_count" label="Signals" align="center" />
                      <th className="px-3 py-3 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((item, i) => (
                      <React.Fragment key={item.company}>
                        <tr className="border-t border-gray-100 hover:bg-[#f8f6f0] transition-colors cursor-pointer"
                          onClick={() => setExpanded(expanded === i ? null : i)} data-testid={`expansion-row-${i}`}>
                          <td className="px-3 py-3 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-3 py-3"><div className="flex items-center gap-2"><WatchlistButton company={item.company} sourceModule="expansion-signals" /><span className="font-semibold text-[#0A1F44]">{item.company}</span></div></td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <div className="w-10 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${item.expansion_score}%` }} />
                              </div>
                              <span className="text-xs font-bold">{item.expansion_score}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold border ${LABEL_STYLES[item.expansion_label] || LABEL_STYLES['Early Signals']}`}>{item.expansion_label}</span>
                          </td>
                          <td className="px-3 py-3 text-right font-medium">{item.total_jobs_30d}</td>
                          <td className="px-3 py-3 text-right text-gray-600">{item.cities_count}</td>
                          <td className="px-3 py-3 text-right text-gray-600">{item.states_count}</td>
                          <td className="px-3 py-3 text-right text-gray-600">{item.departments_count}</td>
                          <td className="px-3 py-3">{item.industry ? <div className="flex flex-col gap-1"><span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200 w-fit">{item.industry}</span><CompanyTypeBadge type={item.company_type} /></div> : '—'}</td>
                          <td className="px-3 py-3 text-center text-xs font-bold text-emerald-700">{item.signal_count}</td>
                          <td className="px-3 py-3 text-center">{expanded === i ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}</td>
                        </tr>
                        {expanded === i && (
                          <tr className="bg-emerald-50/30">
                            <td colSpan={11} className="px-6 py-4">
                              <div className="space-y-2">
                                {item.signals.map((sig, j) => (
                                  <div key={j} className={`flex items-center justify-between px-4 py-2 rounded-lg border ${SIGNAL_TYPE_COLORS[sig.type] || 'bg-gray-50 border-gray-200'}`}>
                                    <div>
                                      <span className="text-xs font-bold">{sig.type}</span>
                                      <span className="text-xs text-gray-500 ml-2">{sig.detail}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="w-16 h-1.5 bg-white/50 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${sig.strength}%` }} /></div>
                                      <span className="text-xs font-bold">{sig.strength}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default ExpansionSignals;
